import { z } from 'zod';

import { CashDenominationRowsSchema } from './cash-denominations.schema.js';
import {
  type CancelCashTreasuryCountRequest,
  type CancelCashTreasuryCountResponse,
  type CashTreasuryCountDiscrepancyDto,
  type CashTreasuryCountDiscrepancyStatus,
  type CashTreasuryCountDraftDto,
  type CashTreasuryCountDraftStatus,
  type CashTreasuryCountDto,
  type CashTreasuryCountMode,
  type CashTreasuryCountStatus,
  type CompleteCashTreasuryCountRequest,
  type CompleteCashTreasuryCountResponse,
  type GetCashTreasuryCountRequest,
  type GetCashTreasuryCountResponse,
  type ListCashTreasuryCountsRequest,
  type ListCashTreasuryCountsResponse,
  type StartCashTreasuryCountRequest,
  type StartCashTreasuryCountResponse,
  cashTreasuryCountDiscrepancyStatusValues,
  cashTreasuryCountDraftStatusValues,
  cashTreasuryCountModeValues,
  cashTreasuryCountStatusValues,
} from './cash-treasury-counts.dto.js';
import { CASH_TREASURY_COUNT_DENOMINATION_TOTAL_MISMATCH } from './cash-error-codes.js';

const timestampSchema = z.iso.datetime();
const noteSchema = z.string().trim().min(1).max(500);
const lockTokenSchema = z.string().trim().min(32).max(256);
const idempotencyKeySchema = z.string().trim().min(1).max(120);

export const CashTreasuryCountModeSchema: z.ZodType<CashTreasuryCountMode> = z.enum(
  cashTreasuryCountModeValues,
);

export const CashTreasuryCountDraftStatusSchema: z.ZodType<CashTreasuryCountDraftStatus> = z.enum(
  cashTreasuryCountDraftStatusValues,
);

export const CashTreasuryCountStatusSchema: z.ZodType<CashTreasuryCountStatus> = z.enum(
  cashTreasuryCountStatusValues,
);

export const CashTreasuryCountDiscrepancyStatusSchema: z.ZodType<CashTreasuryCountDiscrepancyStatus> =
  z.enum(cashTreasuryCountDiscrepancyStatusValues);

export const CashTreasuryCountDraftDtoSchema: z.ZodType<CashTreasuryCountDraftDto> = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  treasuryCashLocationId: z.uuid(),
  mode: CashTreasuryCountModeSchema,
  status: CashTreasuryCountDraftStatusSchema,
  startedByUserId: z.uuid(),
  startedAt: timestampSchema,
  expiresAt: timestampSchema,
  expectedBalanceMinor: z.number().int(),
  expectedBalanceAsOf: timestampSchema,
  ledgerMovementCount: z.number().int().nonnegative(),
  ledgerLastMovementAt: timestampSchema.nullable(),
  ledgerLastMovementUpdatedAt: timestampSchema.nullable(),
  version: z.number().int().positive(),
  completedCountId: z.uuid().nullable(),
  completedAt: timestampSchema.nullable(),
  cancelledAt: timestampSchema.nullable(),
  cancelledByUserId: z.uuid().nullable(),
  cancelReason: noteSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export const CashTreasuryCountDiscrepancyDtoSchema: z.ZodType<CashTreasuryCountDiscrepancyDto> =
  z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    countId: z.uuid(),
    treasuryCashLocationId: z.uuid(),
    expectedAmountMinor: z.number().int(),
    countedAmountMinor: z.number().int().nonnegative(),
    differenceMinor: z.number().int(),
    status: CashTreasuryCountDiscrepancyStatusSchema,
    createdByUserId: z.uuid(),
    createdAt: timestampSchema,
    resolvedAt: timestampSchema.nullable(),
    resolvedByUserId: z.uuid().nullable(),
    resolutionNote: noteSchema.nullable(),
    resolutionMovementId: z.uuid().nullable(),
  });

export const CashTreasuryCountDtoSchema: z.ZodType<CashTreasuryCountDto> = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  treasuryCashLocationId: z.uuid(),
  draftId: z.uuid(),
  mode: CashTreasuryCountModeSchema,
  status: CashTreasuryCountStatusSchema,
  expectedBalanceMinor: z.number().int(),
  expectedBalanceAsOf: timestampSchema,
  countedAmountMinor: z.number().int().nonnegative(),
  differenceMinor: z.number().int(),
  denominations: CashDenominationRowsSchema.nullable(),
  sourceSnapshotId: z.uuid().nullable(),
  sourceBaselineId: z.uuid().nullable(),
  countedByUserId: z.uuid(),
  countedAt: timestampSchema,
  note: noteSchema.nullable(),
  idempotencyKey: idempotencyKeySchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
  discrepancy: CashTreasuryCountDiscrepancyDtoSchema.nullable(),
});

export const StartCashTreasuryCountRequestSchema: z.ZodType<StartCashTreasuryCountRequest> = z
  .object({
    companyId: z.uuid(),
    operationId: z.uuid(),
    treasuryCashLocationId: z.uuid(),
    mode: CashTreasuryCountModeSchema,
    occurredAt: timestampSchema.optional(),
  })
  .strict();

export const StartCashTreasuryCountResponseSchema: z.ZodType<StartCashTreasuryCountResponse> =
  z.object({
    draft: CashTreasuryCountDraftDtoSchema,
    expectedBalanceMinor: z.number().int(),
    lockToken: lockTokenSchema,
    denominationRowsRequired: z.boolean(),
    denominationInventoryActive: z.boolean(),
  });

export const CompleteCashTreasuryCountRequestSchema: z.ZodType<CompleteCashTreasuryCountRequest> = z
  .object({
    companyId: z.uuid(),
    operationId: z.uuid(),
    draftId: z.uuid(),
    countedAmountMinor: z.number().int().nonnegative(),
    denominations: CashDenominationRowsSchema.nullable().optional(),
    lockToken: lockTokenSchema,
    version: z.number().int().positive(),
    idempotencyKey: idempotencyKeySchema,
    note: noteSchema.nullable().optional(),
    occurredAt: timestampSchema.optional(),
  })
  .strict()
  .superRefine((value, context) => {
    if (!value.denominations) {
      return;
    }

    const denominationTotalMinor = value.denominations.reduce(
      (total, row) => total + row.denom * row.count,
      0,
    );
    if (denominationTotalMinor !== value.countedAmountMinor) {
      context.addIssue({
        code: 'custom',
        message: 'Denomination total does not match counted amount.',
        path: ['denominations'],
        params: { apiErrorCode: CASH_TREASURY_COUNT_DENOMINATION_TOTAL_MISMATCH },
      });
    }
  });

export const CompleteCashTreasuryCountResponseSchema: z.ZodType<CompleteCashTreasuryCountResponse> =
  z.object({
    count: CashTreasuryCountDtoSchema,
  });

export const CancelCashTreasuryCountRequestSchema: z.ZodType<CancelCashTreasuryCountRequest> = z
  .object({
    companyId: z.uuid(),
    operationId: z.uuid(),
    draftId: z.uuid(),
    lockToken: lockTokenSchema.optional(),
    override: z.boolean().optional(),
    reason: noteSchema.nullable().optional(),
    occurredAt: timestampSchema.optional(),
  })
  .strict();

export const CancelCashTreasuryCountResponseSchema: z.ZodType<CancelCashTreasuryCountResponse> =
  z.object({
    draft: CashTreasuryCountDraftDtoSchema,
  });

export const ListCashTreasuryCountsRequestSchema: z.ZodType<ListCashTreasuryCountsRequest> = z
  .object({
    companyId: z.uuid(),
    treasuryCashLocationId: z.uuid().optional(),
    mode: CashTreasuryCountModeSchema.optional(),
    from: timestampSchema.optional(),
    to: timestampSchema.optional(),
  })
  .strict();

export const ListCashTreasuryCountsResponseSchema: z.ZodType<ListCashTreasuryCountsResponse> =
  z.object({
    items: z.array(CashTreasuryCountDtoSchema),
  });

export const GetCashTreasuryCountRequestSchema: z.ZodType<GetCashTreasuryCountRequest> = z
  .object({
    companyId: z.uuid(),
    countId: z.uuid(),
  })
  .strict();

export const GetCashTreasuryCountResponseSchema: z.ZodType<GetCashTreasuryCountResponse> = z.object(
  {
    count: CashTreasuryCountDtoSchema,
  },
);
